import React, { useState, useEffect } from 'react';
import { Bell, Building2, Check, Copy, ExternalLink, Globe, Link as LinkIcon, Mail, MapPin, Phone, SquarePen, User } from 'lucide-react';
import { Formik, Form, Field, FieldArray, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { getClinicProfile, updateClinicProfile } from '../Services/APIs/authentication';
import { getClinicDetails, setClinicDetails } from '../Utils/clinicAuth';
import { buildCompany, companySlugSchema, extractEditableSlug, getClaraviewLink, SLUG_MAX_LENGTH } from '../Utils/claraviewLink';
import ManageBillingButton from './Subscription/ManageBillingButton';
import ConfirmDialog from '../Components/UI/ConfirmDialog';
import LogoutButton from '../Components/UI/LogoutButton';

const PHONE_REGEX = /^[0-9+\-\s()]+$/;
const WEBSITE_REGEX = /^(https?:\/\/)?[\w-]+(\.[\w-]+)+(\/\S*)?$/i;
const MAX_NOTIFICATION_EMAILS = 10;

const INPUT_CLASSES =
    'w-full px-4 py-3 bg-white border border-[#E8E6E3] rounded-lg text-[#2D2D2D] focus:outline-none focus:ring-2 focus:ring-[#38433A]';

const ERROR_CLASSES = 'text-xs text-red-600 mt-1';

const lengthRule = (min, max, message) =>
    Yup.string().trim().test('length', message, (value) => !value || (value.length >= min && value.length <= max));

const profileSchema = Yup.object().shape({
    doctor_name: lengthRule(2, 100, 'Full name must be between 2 and 100 characters'),
    practise_name: lengthRule(2, 100, 'Practice name must be between 2 and 100 characters'),
    phone_number: lengthRule(10, 20, 'Phone number must be between 10 and 20 characters')
        .matches(PHONE_REGEX, { message: 'Phone number can only contain digits, spaces and + - ( )', excludeEmptyString: true }),
    address: lengthRule(5, 500, 'Address must be between 5 and 500 characters'),
    website: Yup.string()
        .trim()
        .matches(WEBSITE_REGEX, { message: 'Enter a valid website (e.g. example.com)', excludeEmptyString: true }),
    email: Yup.array()
        .of(Yup.string().trim().email('Enter a valid email address').required('Email is required'))
        .min(1, 'At least one notification email is required'),
    is_unsubscribed: Yup.boolean(),
});

const linkSchema = Yup.object().shape({ slug: companySlugSchema });

const toFormValues = (clinic) => ({
    doctor_name: clinic?.doctor_name || '',
    practise_name: clinic?.practise_name || '',
    email: Array.isArray(clinic?.email) && clinic.email.length ? clinic.email : [''],
    phone_number: clinic?.phone_number || '',
    address: clinic?.address || '',
    website: clinic?.website || '',
    is_unsubscribed: Boolean(clinic?.is_unsubscribed),
});

const changedFields = (values, initial) =>
    Object.keys(values).reduce((changed, key) => {
        if (JSON.stringify(values[key]) !== JSON.stringify(initial[key])) changed[key] = values[key];
        return changed;
    }, {});

/* Billing card is temporarily turned off — flip to true to bring it back. */
const SHOW_BILLING = false;

/* Every section-level action on this page shares one weight — Edit Profile, Customize Link and
 * Manage Billing are peers, none of them the page's primary action. */
const SECTION_ACTION_CLASSES =
    'inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-white text-[#38433A] border border-[#38433A] rounded-lg hover:bg-[#F5F3F0] transition-colors';

/* Logout reads as an ordinary quiet control until you reach for it, then turns destructive.
 * Permanent red in the page header made a routine settings screen feel like a warning. */
const LOGOUT_CLASSES =
    'inline-flex w-full sm:w-auto items-center justify-center gap-2 h-11 px-5 text-sm rounded-lg border border-[#E8E6E3] text-[#6B6B6B] transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-200';

const SectionCard = ({ title, children }) => (
    <div className="bg-white rounded-2xl border border-[#E8E6E3] shadow-sm overflow-hidden">
        <div className="border-b border-[#E8E6E3] px-5 md:px-8 py-[18px]">
            <h2 className="text-[18px] text-[#2D2D2D]">{title}</h2>
        </div>
        <div className="px-5 md:px-8 py-6">{children}</div>
    </div>
);

const FieldLabel = ({ icon: Icon, children }) => (
    <label className="flex items-center gap-2 text-sm text-[#6B6B6B] mb-2">
        {Icon && <Icon className="w-4 h-4 shrink-0" />}
        {children}
    </label>
);

const ReadOnlyField = ({ icon, label, value }) => (
    <div>
        <FieldLabel icon={icon}>{label}</FieldLabel>
        <p className="text-[#2D2D2D] px-4 py-3 bg-[#FAFAF9] rounded-lg break-words">{value || 'N/A'}</p>
    </div>
);

const AccountSettings = () => {
    const [adminData, setAdminData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState('');

    // Claraview link edit state
    const [isEditingLink, setIsEditingLink] = useState(false);
    const [linkError, setLinkError] = useState(null);
    const [savingLink, setSavingLink] = useState(false);
    const [copied, setCopied] = useState(false);
    const [pendingSlug, setPendingSlug] = useState(null);

    const getLink = () => getClaraviewLink(adminData?.company);

    const handleCopyLink = () => {
        navigator.clipboard.writeText(getLink());
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    useEffect(() => {
        const fetchClinicProfile = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await getClinicProfile();
                const clinic = response.data?.data;
                if (clinic) {
                    setAdminData(clinic);
                    setClinicDetails(clinic);
                }
            } catch (err) {
                console.error('Error fetching clinic profile:', err);
                setError(err?.response?.data?.message || 'Failed to load account details');
            } finally {
                setLoading(false);
            }
        };

        fetchClinicProfile();
    }, []);

    const applySaved = (savedClinic) => {
        setAdminData(savedClinic);
        setClinicDetails({ ...getClinicDetails(), ...savedClinic });
    };

    const handleSave = async (values, { setSubmitting }) => {
        setSaveError(null);
        setSaveSuccess('');

        const changed = changedFields(values, toFormValues(adminData));
        if (!Object.keys(changed).length) {
            setIsEditing(false);
            setSubmitting(false);
            return;
        }

        try {
            const response = await updateClinicProfile(changed);
            applySaved(response.data?.data || { ...adminData, ...changed });
            setSaveSuccess(response.data?.message || 'Your details have been saved.');
            setTimeout(() => { setSaveSuccess(''); setIsEditing(false); }, 3000);
        } catch (err) {
            console.error('Error updating clinic profile:', err);
            setSaveError(err?.response?.data?.message || err?.data?.message || 'Failed to update profile');
        }
        setSubmitting(false);
    };

    // Handle edit link
    const handleEditLink = () => {
        setIsEditingLink(true);
        setLinkError(null);
    };

    const handleRequestSaveLink = ({ slug }, { setSubmitting }) => {
        setLinkError(null);
        setPendingSlug(slug);
        setSubmitting(false);
    };

    // Handle save link — sends only `company` (not a full-object reconstruction of every other
    // field from a stale snapshot), so this can't race with handleSave and revert its changes.
    const handleSaveLink = async () => {
        const newCompany = buildCompany(pendingSlug);
        setPendingSlug(null);
        setSavingLink(true);
        setLinkError(null);

        try {
            const response = await updateClinicProfile({ company: newCompany });
            applySaved(response.data?.data || { ...adminData, company: newCompany });
            setIsEditingLink(false);
        } catch (err) {
            console.error('Error updating link:', err);
            setLinkError(err?.response?.data?.message || err?.data?.message || 'Failed to update link');
        } finally {
            setSavingLink(false);
        }
    };

    // Handle cancel link edit
    const handleCancelLink = () => {
        setIsEditingLink(false);
        setLinkError(null);
        setPendingSlug(null);
    };

    const signedInAs = adminData?.doctor_name || adminData?.email?.[0] || '';

    return (
        <main className="flex-1 overflow-auto">
            <div className="mx-auto w-full max-w-7xl flex flex-col gap-4 md:gap-6 px-4 md:px-8 pt-5 md:pt-8 pb-6">
                <header>
                    <h1 className="text-2xl md:text-[32px] text-[#2D2D2D] tracking-tight">Account Settings</h1>
                    <p className="mt-1.5 text-sm text-[#6B6B6B]">
                        {SHOW_BILLING
                            ? 'Manage your practice profile, simulation link, and billing.'
                            : 'Manage your practice profile and simulation link.'}
                    </p>
                </header>

                {error && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                        {error}
                    </div>
                )}

                <SectionCard title="Profile Information">
                    {loading ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D2D2D]" />
                        </div>
                    ) : isEditing ? (
                        <Formik
                            enableReinitialize
                            initialValues={toFormValues(adminData)}
                            validationSchema={profileSchema}
                            onSubmit={handleSave}
                        >
                            {({ values, isSubmitting, setFieldValue, resetForm }) => (
                                <Form>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                        <div>
                                            <FieldLabel>Full Name</FieldLabel>
                                            <Field type="text" name="doctor_name" className={INPUT_CLASSES} placeholder="Enter full name" />
                                            <ErrorMessage name="doctor_name" component="div" className={ERROR_CLASSES} />
                                        </div>
                                        <div>
                                            <FieldLabel>Practice Name</FieldLabel>
                                            <Field type="text" name="practise_name" className={INPUT_CLASSES} placeholder="Enter practice name" />
                                            <ErrorMessage name="practise_name" component="div" className={ERROR_CLASSES} />
                                        </div>
                                        <div className="md:col-span-2">
                                            <FieldLabel>Email Address(es)</FieldLabel>
                                            <FieldArray name="email">
                                                {({ push, remove }) => (
                                                    <div className="space-y-2">
                                                        {values.email.map((_, index) => (
                                                            <div key={index}>
                                                                <div className="flex gap-2">
                                                                    <Field
                                                                        type="email"
                                                                        name={`email.${index}`}
                                                                        className={`flex-1 min-w-0 ${INPUT_CLASSES}`}
                                                                        placeholder="Enter email address"
                                                                    />
                                                                    {values.email.length > 1 && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => remove(index)}
                                                                            className="shrink-0 px-3 py-3 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                                                                        >
                                                                            Remove
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                <ErrorMessage name={`email.${index}`} component="div" className={ERROR_CLASSES} />
                                                            </div>
                                                        ))}
                                                        <button
                                                            type="button"
                                                            onClick={() => push('')}
                                                            disabled={values.email.length >= MAX_NOTIFICATION_EMAILS}
                                                            className="px-4 py-2 text-sm bg-white text-[#38433A] border border-[#38433A] rounded-lg hover:bg-[#F5F3F0] transition-colors mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            + Add Email
                                                        </button>
                                                        <ErrorMessage name="email" component="div" className={ERROR_CLASSES} />
                                                    </div>
                                                )}
                                            </FieldArray>
                                        </div>
                                        <div>
                                            <FieldLabel>Phone Number</FieldLabel>
                                            <Field type="tel" name="phone_number" className={INPUT_CLASSES} placeholder="Enter phone number" />
                                            <ErrorMessage name="phone_number" component="div" className={ERROR_CLASSES} />
                                        </div>
                                        <div className="md:col-span-2">
                                            <FieldLabel>Practice Address</FieldLabel>
                                            <Field
                                                as="textarea"
                                                name="address"
                                                rows="3"
                                                className={`resize-none ${INPUT_CLASSES}`}
                                                placeholder="Enter practice address"
                                            />
                                            <ErrorMessage name="address" component="div" className={ERROR_CLASSES} />
                                        </div>
                                        <div className="md:col-span-2">
                                            <FieldLabel>Website</FieldLabel>
                                            <Field type="text" name="website" className={INPUT_CLASSES} placeholder="example.com" />
                                            <ErrorMessage name="website" component="div" className={ERROR_CLASSES} />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="flex items-start gap-3 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={!values.is_unsubscribed}
                                                    onChange={(e) => setFieldValue('is_unsubscribed', !e.target.checked)}
                                                    className="mt-0.5 h-4 w-4 shrink-0 accent-[#38433A]"
                                                />
                                                <span className="text-sm text-[#2D2D2D]">
                                                    Send scan notifications to the addresses above
                                                    <span className="block text-[#6B6B6B]">
                                                        Turning this off stops notifications for everyone at this practice.
                                                    </span>
                                                </span>
                                            </label>
                                        </div>
                                    </div>

                                    {saveSuccess && (
                                        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                                            {saveSuccess}
                                        </div>
                                    )}

                                    {saveError && (
                                        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                                            {saveError}
                                        </div>
                                    )}

                                    <div className="mt-6 flex flex-wrap justify-end gap-3">
                                        <button
                                            type="button"
                                            onClick={() => { resetForm(); setIsEditing(false); setSaveError(null); }}
                                            className="px-6 py-2.5 bg-white text-[#38433A] border border-[#E8E6E3] rounded-lg hover:bg-[#FAFAF9] transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="px-6 py-2.5 bg-[#38433A] text-white rounded-lg hover:bg-[#2C352E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                                    Saving...
                                                </>
                                            ) : (
                                                'Save Changes'
                                            )}
                                        </button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                <ReadOnlyField icon={User} label="Full Name" value={adminData?.doctor_name} />
                                <ReadOnlyField icon={Building2} label="Practice Name" value={adminData?.practise_name} />

                                <div>
                                    <FieldLabel icon={Mail}>Email Address</FieldLabel>
                                    <div className="space-y-2">
                                        {Array.isArray(adminData?.email) && adminData.email.length > 0 ? (
                                            adminData.email.map((email, index) => (
                                                <p key={index} className="text-[#2D2D2D] px-4 py-3 bg-[#FAFAF9] rounded-lg break-all">
                                                    {email}
                                                </p>
                                            ))
                                        ) : (
                                            <p className="text-[#2D2D2D] px-4 py-3 bg-[#FAFAF9] rounded-lg">N/A</p>
                                        )}
                                    </div>
                                </div>

                                <ReadOnlyField icon={Phone} label="Phone Number" value={adminData?.phone_number} />

                                <div className="md:col-span-2">
                                    <ReadOnlyField icon={MapPin} label="Practice Address" value={adminData?.address} />
                                </div>
                                <div className="md:col-span-2">
                                    <ReadOnlyField icon={Globe} label="Website" value={adminData?.website} />
                                </div>
                                <div className="md:col-span-2">
                                    <ReadOnlyField
                                        icon={Bell}
                                        label="Scan Notifications"
                                        value={adminData?.is_unsubscribed ? 'Off' : 'On'}
                                    />
                                </div>
                            </div>

                            <div className="mt-6 flex justify-end">
                                <button onClick={() => setIsEditing(true)} className={SECTION_ACTION_CLASSES}>
                                    <SquarePen className="w-4 h-4" />
                                    Edit Profile
                                </button>
                            </div>
                        </>
                    )}
                </SectionCard>

                <SectionCard title="Claraview Link">
                    {linkError && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                            {linkError}
                        </div>
                    )}

                    <div className="space-y-4">
                        <div>
                            <FieldLabel icon={LinkIcon}>Your Claraview Link</FieldLabel>

                            {!isEditingLink ? (
                                <div className="flex flex-col sm:flex-row gap-2.5">
                                    <input
                                        type="text"
                                        readOnly
                                        className="flex-1 min-w-0 px-4 py-3 bg-[#FAFAF9] border border-[#E8E6E3] rounded-lg text-[#2D2D2D] focus:outline-none"
                                        value={getLink()}
                                    />
                                    <button
                                        onClick={handleCopyLink}
                                        className="shrink-0 px-6 py-3 bg-[#38433A] text-white rounded-lg hover:bg-[#2C352E] transition-colors flex items-center justify-center gap-2 sm:min-w-[120px]"
                                    >
                                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                        {copied ? 'Link copied' : 'Copy Link'}
                                    </button>
                                </div>
                            ) : (
                                <Formik
                                    enableReinitialize
                                    initialValues={{ slug: extractEditableSlug(adminData?.company) }}
                                    validationSchema={linkSchema}
                                    onSubmit={handleRequestSaveLink}
                                >
                                    {({ isSubmitting }) => (
                                        <Form className="space-y-3">
                                            {/* Prefix stacks above the slug on phones — side by side it leaves no room to type */}
                                            <div className="flex flex-col sm:flex-row border border-[#E8E6E3] rounded-lg overflow-hidden">
                                                <span className="px-4 py-3 bg-[#FAFAF9] text-[#6B6B6B] text-sm whitespace-nowrap">
                                                    https://app.claraview.ai/
                                                </span>
                                                <Field
                                                    type="text"
                                                    name="slug"
                                                    maxLength={SLUG_MAX_LENGTH}
                                                    className="flex-1 min-w-0 px-4 py-3 bg-white text-[#2D2D2D] border-t sm:border-t-0 sm:border-l border-[#E8E6E3] focus:outline-none focus:ring-2 focus:ring-[#38433A]"
                                                    placeholder="e.g., john"
                                                />
                                            </div>
                                            <ErrorMessage name="slug" component="div" className={ERROR_CLASSES} />

                                            {adminData?.company && !extractEditableSlug(adminData.company) && (
                                                <p className="text-xs text-[#6B6B6B]">
                                                    Current link: {getLink()}
                                                </p>
                                            )}

                                            <div className="flex gap-3">
                                                <button
                                                    type="button"
                                                    onClick={handleCancelLink}
                                                    className="flex-1 px-4 py-2 text-sm bg-white text-[#38433A] border border-[#E8E6E3] rounded-lg hover:bg-[#FAFAF9] transition-colors"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="submit"
                                                    disabled={isSubmitting || savingLink}
                                                    className="flex-1 px-4 py-2 text-sm bg-[#38433A] text-white rounded-lg hover:bg-[#2C352E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                                >
                                                    {savingLink ? (
                                                        <>
                                                            <div className="animate-spin rounded-full h-3 w-3 border-b border-white" />
                                                            Saving...
                                                        </>
                                                    ) : (
                                                        'Save Link'
                                                    )}
                                                </button>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            )}
                        </div>

                        <p className="text-sm text-[#6B6B6B]">
                            Share this link with potential patients to collect smile photos and generate previews through Claraview
                        </p>

                        {!isEditingLink && (
                            <button onClick={handleEditLink} className={`w-full sm:w-auto ${SECTION_ACTION_CLASSES}`}>
                                Customize Link
                            </button>
                        )}
                    </div>

                    {pendingSlug !== null && (
                        <ConfirmDialog
                            variant="light"
                            title="Change your link?"
                            message="Any link or QR code you've already shared with patients will stop working, and another clinic can claim your old link."
                            confirmLabel="Change Link"
                            cancelLabel="Keep Current"
                            onConfirm={handleSaveLink}
                            onCancel={() => setPendingSlug(null)}
                        />
                    )}
                </SectionCard>

                {/* Billing gets a card of its own so the action has context and sits in the same
                  * rhythm as the other sections, instead of floating in the page header. */}
                {SHOW_BILLING && (
                    <SectionCard title="Billing">
                        <p className="text-sm leading-6 text-[#6B6B6B] max-w-2xl">
                            Your payment method, invoices, and plan are managed in our secure billing portal.
                            You&apos;ll be taken to Stripe and returned here when you&apos;re done.
                        </p>
                        <div className="mt-5">
                            <ManageBillingButton
                                icon={ExternalLink}
                                className={`w-full sm:w-auto ${SECTION_ACTION_CLASSES} disabled:opacity-50 disabled:cursor-not-allowed`}
                            />
                        </div>
                    </SectionCard>
                )}

                {/* Session footer — the conventional home for sign-out on a settings page: findable,
                  * out of the way of everything above it, and clear about whose session it ends. */}
                <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-[#E8E6E3] pt-6">
                    {signedInAs ? (
                        <p className="text-sm text-[#6B6B6B]">
                            Signed in as <span className="text-[#2D2D2D] break-all">{signedInAs}</span>
                        </p>
                    ) : (
                        <span />
                    )}
                    <LogoutButton
                        variant="light"
                        className={LOGOUT_CLASSES}
                        confirmMessage="You'll need to sign in again to access your dashboard."
                    />
                </div>
            </div>
        </main>
    );
};

export default AccountSettings;
